import { useCallback } from 'react';

import {
  DropdownProps,
  Skeleton,
  MultiAutocompleteBadgeCount,
} from '@cast/design-system';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { useNamespacesQuery } from 'hooks/queries/cost';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { useSearchCriterion } from '@cast/shared-search-ui';

type Props = Partial<
  Pick<DropdownProps<string, string, 'multiple'>, 'onOpen'>
> & {
  onApply?: (values: string[]) => void;
  size?: 'small' | 'medium' | 'large';
};

export const NamespacesFilter = ({ onOpen, onApply, size }: Props) => {
  const { cluster } = useCluster();
  const { adjustedDateRange } = useCostReportContext();

  const { setValue, value, clear } = useSearchCriterion('namespaces');

  const { isLoading, namespaces } = useNamespacesQuery({
    clusterIds: [cluster.id],
    range: adjustedDateRange,
  });

  const onChange = useCallback(
    (values: string[]) => {
      if (values.length > 0) {
        setValue?.(values);
        onApply?.(values);
      } else {
        clear?.();
      }
    },
    [clear, onApply, setValue]
  );

  if (isLoading) {
    return <Skeleton height={32} width="100%" />;
  }

  return (
    <MultiAutocompleteBadgeCount
      showSelectedOptionsFirst
      popoverSize={{ maxHeight: 400, maxWidth: 350 }}
      showSelectAllItem
      onChange={({ values }) => onChange(values)}
      onOpen={onOpen}
      value={value || []}
      className="tour-highlight"
      placeholder="Namespaces"
      indicatorLabel="Namespaces"
      indicatorColor="indigo"
      items={namespaces || []}
      size={size}
    />
  );
};
